import { useEffect, useState, useMemo } from 'react';
import { Bell, LogOut, ChevronDown, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { handleLogout } from '../../utils';
import {
  getCahbookUser,
  getSelectedAccount,
  setSelectedAccount,
  getSelectedBook,
  setSelectedBook,
} from '../../Storage/Storage';
import logo from '../../Images/logo.png';
import AppDropDown from '../AppDropDown/AppDropDown';

function Header({ onMenuClick, hideSideBar, isMobile }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccountState] = useState(null);
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBookState] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  // ✅ Memoize user so it doesn't change on every render
  const user = useMemo(() => getCahbookUser(), []);

  // ✅ Load user accounts and previously selected account
  useEffect(() => {
    const savedAccount = getSelectedAccount();

    if (user?.accountId) {
      const userAccounts = Array.isArray(user.accountId)
        ? user.accountId
        : [user.accountId];

      setAccounts(userAccounts);
      const accountToSet = savedAccount || userAccounts[0];
      setSelectedAccountState(accountToSet);

      // If no saved account, set the first one
      if (!savedAccount && userAccounts[0]) {
        setSelectedAccount(userAccounts[0]);
      }
    }
  }, [user]);

  // ✅ Filter books based on selected account
  useEffect(() => {
    if (selectedAccount && user?.bookId) {
      const userBooks = Array.isArray(user.bookId)
        ? user.bookId
        : [user.bookId];

      // Filter books by selected account ID
      const filteredBooks = userBooks.filter(
        (book) => book.accountId === selectedAccount._id || book.accountId?._id === selectedAccount._id
      );

      setBooks(filteredBooks);

      // Auto-select first book or get saved book if it's in filtered list
      const savedBook = getSelectedBook();
      const isBookInFilteredList = filteredBooks.some(
        (book) => book._id === savedBook?._id
      );

      if (savedBook && isBookInFilteredList) {
        setSelectedBookState(savedBook);
      } else if (filteredBooks.length > 0) {
        setSelectedBookState(filteredBooks[0]);
        setSelectedBook(filteredBooks[0]);
      } else {
        setSelectedBookState(null);
        setSelectedBook(null);
      }
    }
  }, [selectedAccount, user?.bookId]);

  // ✅ Handle account change
  const handleAccountChange = (accountId) => {
    const selected = accounts.find((acc) => acc._id === accountId);
    if (selected) {
      setSelectedAccountState(selected);
      setSelectedAccount(selected); // Save to local storage
      window.location.reload(); // Refresh to apply new account context
    }
  };

  // ✅ Handle book change
  const handleBookChange = (bookId) => {
    const selected = books.find((book) => book._id === bookId);
    if (selected) {
      setSelectedBookState(selected);
      setSelectedBook(selected); // Save to local storage
      window.location.reload(); // Refresh to apply new book context
    }
  };

  // ✅ Prepare dropdown options
  const accountOptions = accounts.map((acc) => ({
    label: acc.accountName,
    onClick: () => handleAccountChange(acc._id),
  }));

  const bookOptions = books.map((book) => ({
    label: book.bookName,
    onClick: () => handleBookChange(book._id),
  }));

  // ✅ Dropdown title logic
  const getDropdownTitle = () => {
    if (loadingAccounts) return 'Loading...';
    if (selectedAccount) return selectedAccount.accountName || 'Select Account';
    return 'Select Account';
  };

  const getBookDropdownTitle = () => {
    if (loadingAccounts) return 'Loading...';
    if (selectedBook) return selectedBook.bookName || 'Select Book';
    return 'Select Book';
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 shadow-sm z-20">
      <div className="flex items-center justify-between px-4 md:px-6 py-2 md:py-3">
        {/* Left Section - Menu & Logo */}
        <div className="flex items-center gap-4 flex-1">
          {/* Desktop Menu Button (hidden on mobile since we use bottom nav) */}
          {!hideSideBar && !isMobile && (
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
          )}

          {/* Logo - show on mobile or when sidebar hidden */}
          {(hideSideBar || isMobile) && (
            <div className="flex items-center">
              <img src={logo} alt="Logo" className="h-8 md:h-10 object-contain" />
            </div>
          )}
        </div>

        {/* Right Section - Dropdowns and Profile */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Account Dropdown - Hidden on mobile, shown in profile menu instead */}
          {accountOptions.length > 1 && (
            <div className="hidden md:flex">
              <AppDropDown title={getDropdownTitle()} options={accountOptions} />
            </div>
          )}

          {/* Book Dropdown - Hidden on mobile, shown in profile menu instead */}
          {bookOptions.length > 1 && (
            <div className="hidden md:flex">
              <AppDropDown title={getBookDropdownTitle()} options={bookOptions} />
            </div>
          )}

          {/* Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowProfile((prev) => !prev)}
              className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-semibold text-gray-800">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.roleId?.roleName || 'User'}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-600" />
            </button>

            {/* Profile Menu */}
            {showProfile && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-40">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-800">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.email || 'example@gmail.com'}
                  </p>
                </div>

                {/* Account Selection - Only on mobile when multiple accounts exist */}
                {accountOptions.length > 1 && (
                  <div className="md:hidden border-b border-gray-100">
                    <div className="px-4 py-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Switch Account
                      </p>
                      <div className="space-y-1">
                        {accounts.map((acc) => (
                          <button
                            key={acc._id}
                            onClick={() => {
                              handleAccountChange(acc._id);
                              setShowProfile(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                              selectedAccount?._id === acc._id
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="truncate">{acc.accountName}</span>
                              {selectedAccount?._id === acc._id && (
                                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Book Selection - Only on mobile when multiple books exist */}
                {bookOptions.length > 1 && (
                  <div className="md:hidden border-b border-gray-100">
                    <div className="px-4 py-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Switch Book
                      </p>
                      <div className="space-y-1">
                        {books.map((book) => (
                          <button
                            key={book._id}
                            onClick={() => {
                              handleBookChange(book._id);
                              setShowProfile(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                              selectedBook?._id === book._id
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="truncate">{book.bookName}</span>
                              {selectedBook?._id === book._id && (
                                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <button
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors text-red-600 w-full text-left"
                  onClick={() => handleLogout(navigate, dispatch)}
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Close profile dropdown when clicking outside */}
      {showProfile && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowProfile(false)}
        ></div>
      )}
    </header>
  );
}

export default Header;
